
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
    Clock,
    Heart,
    StopCircle,
    Loader2,
    Zap,
    AlertCircle
} from 'lucide-react';
import RichText from '../../components/RichText';
import CachedImage from '../../components/CachedImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlitzChallenge = () => {
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState('start'); // start, quiz, result
    const [blitzSession, setBlitzSession] = useState(null);
    const [blitzQuestion, setBlitzQuestion] = useState(null);
    const [blitzAnswer, setBlitzAnswer] = useState(null);
    const [blitzQuestionNumber, setBlitzQuestionNumber] = useState(1);
    const [blitzStats, setBlitzStats] = useState({ correct: 0, wrong: 0, skipped: 0, saved: 0, total_attempted: 0 });
    const [questionSaved, setQuestionSaved] = useState(false);
    const [timeLeft, setTimeLeft] = useState(144);
    const [questionStartTime, setQuestionStartTime] = useState(Date.now());
    const [result, setResult] = useState(null);

    const startBlitzChallenge = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/challenges/blitz`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setBlitzSession(response.data.session_id);
            setBlitzQuestion(response.data.question);
            setBlitzQuestionNumber(response.data.question_number);
            setBlitzAnswer(null);
            setQuestionSaved(false);
            setTimeLeft(144);
            setQuestionStartTime(Date.now());
            setStep('quiz');

            toast.success("Blitz mode started! Answer as many as you can!");
        } catch (error) {
            console.error('Blitz start error:', error);
            toast.error('Failed to start blitz mode.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Auto start if mounting this component directly implies playing
        startBlitzChallenge();
        // eslint-disable-next-line
    }, []);

    const handleBlitzNext = useCallback(async () => {
        if (!blitzSession || !blitzQuestion) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);

            const response = await axios.post(
                `${API}/challenges/blitz/next?session_id=${blitzSession}&question_id=${blitzQuestion.id}`,
                {
                    selected_option: blitzAnswer,
                    time_taken: timeTaken
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        session_id: blitzSession,
                        question_id: blitzQuestion.id,
                        selected_option: blitzAnswer,
                        time_taken: timeTaken
                    }
                }
            );

            setBlitzQuestion(response.data.question);
            setBlitzQuestionNumber(response.data.question_number);
            setBlitzStats(response.data.current_stats);
            setBlitzAnswer(null);
            setQuestionSaved(false);
            setTimeLeft(144);
            setQuestionStartTime(Date.now());
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to load next question');
        } finally {
            setLoading(false);
        }
    }, [blitzSession, blitzQuestion, blitzAnswer, questionStartTime]);

    useEffect(() => {
        let timer;
        if (step === 'quiz' && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleBlitzNext();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [step, timeLeft, handleBlitzNext]);

    const handleBlitzFinish = async () => {
        if (!blitzSession) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/challenges/blitz/finish?session_id=${blitzSession}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { session_id: blitzSession }
                }
            );

            setResult(response.data);
            setStep('result');
            toast.success('Session complete!');
        } catch (error) {
            toast.error('Failed to finish session');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveQuestion = async () => {
        if (!blitzSession || !blitzQuestion) return;

        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API}/challenges/blitz/save-in-session?session_id=${blitzSession}&question_id=${blitzQuestion.id}`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        session_id: blitzSession,
                        question_id: blitzQuestion.id
                    }
                }
            );

            setQuestionSaved(true);
            setBlitzStats(prev => ({ ...prev, saved: prev.saved + 1 }));
            toast.success('Question saved! ❤️');
        } catch (error) {
            toast.error('Failed to save question');
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading && step === 'start') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (step === 'quiz' && blitzQuestion) {
        const isTimeWarning = timeLeft <= 30;
        return (
            <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="container mx-auto max-w-7xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-muted-foreground">
                                Question {blitzQuestionNumber}
                            </span>
                            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                                <Zap className="w-4 h-4" />
                                <span className="text-sm font-medium">Blitz Mode</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSaveQuestion}
                                disabled={questionSaved}
                                className={questionSaved ? 'bg-red-500/10 border-red-500/30' : ''}
                            >
                                <Heart className={`w-4 h-4 ${questionSaved ? 'fill-red-500 text-red-500' : ''}`} />
                            </Button>

                            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary ${isTimeWarning ? 'timer-warning' : ''}`}>
                                <Clock className="w-4 h-4" />
                                <span className="timer-display font-mono">
                                    {formatTime(timeLeft)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Question */}
                    <div className="bg-card border border-border rounded-xl p-6 sm:p-8 mb-6">
                        <h2 className="text-xl sm:text-2xl font-semibold leading-relaxed">
                            <RichText>{blitzQuestion.text}</RichText>
                        </h2>
                        {blitzQuestion.image_url && (
                            <div className="mt-4">
                                <CachedImage src={blitzQuestion.image_url} alt="Question" className="max-h-96 mx-auto rounded-lg" />
                            </div>
                        )}
                    </div>

                    {/* Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
                        {blitzQuestion.options && blitzQuestion.options.map((option, index) => {
                            const optionText = typeof option === 'string' ? option : option.text;
                            const optionImage = typeof option === 'object' ? option.image_url : null;

                            return (
                                <button
                                    key={index}
                                    onClick={() => setBlitzAnswer(index)}
                                    className={`option-button ${blitzAnswer === index ? 'selected' : ''
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-semibold text-base
                            ${blitzAnswer === index
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-secondary'
                                            }`}>
                                            {String.fromCharCode(65 + index)}
                                        </div>
                                        <div className="flex-1 text-left">
                                            {optionText && <span className="text-base"><RichText>{optionText}</RichText></span>}
                                            {optionImage && <CachedImage src={optionImage} alt={`Option ${index + 1}`} className="mt-2 max-h-32 rounded" />}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between">
                        <Button
                            onClick={handleBlitzFinish}
                            variant="outline"
                            disabled={loading}
                        >
                            <StopCircle className="w-4 h-4 mr-2" />
                            Stop & Review
                        </Button>

                        <Button
                            onClick={handleBlitzNext}
                            disabled={loading}
                            className="btn-hover"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Next Question
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'result' && result) {
        return (
            <div className="min-h-screen pt-20 pb-12 px-6">
                <div className="container mx-auto max-w-4xl text-center">
                    <h1 className="text-3xl font-bold mb-6">Session Summary</h1>
                    <div className="grid grid-cols-4 gap-4 mb-8">
                        <div className="stat-card">
                            <div className="text-2xl font-bold">{result.stats.total_attempted}</div>
                            <div>Total</div>
                        </div>
                        <div className="stat-card">
                            <div className="text-2xl font-bold text-green-500">{result.stats.correct}</div>
                            <div>Correct</div>
                        </div>
                        <div className="stat-card">
                            <div className="text-2xl font-bold text-red-500">{result.stats.wrong}</div>
                            <div>Wrong</div>
                        </div>
                        <div className="stat-card">
                            <div className="text-2xl font-bold text-pink-500">{result.stats.saved}</div>
                            <div>Saved</div>
                        </div>
                    </div>
                    <Button onClick={() => window.location.href = '/challenge'}>Back to Menu</Button>
                </div>
            </div>
        )
    }

    return <div>Loading...</div>;
};

export default BlitzChallenge;
